import type { NormalizedCacheObject } from "@apollo/client";
import { ApolloClient, ApolloLink } from "@apollo/client";
import { useSnackbar } from "@fullscript/aviary-web";
import { useMemo } from "react";

import { createClientHttpLink } from "@shared/data/client/links/createClientHttpLink";
import { getErrorLink } from "@shared/data/client/links/errorLink/errorLink";
import { getAddEventIdLink } from "@shared/data/client/links/getAddEventIdLink/getAddEventIdLink";
import { getAddUTMLink } from "@shared/data/client/links/getAddUTMLink/getAddUTMLink";
import { fingerprintLink } from "@shared/data/client/links/getFingerprintLink/getFingerprintLink";
import { getFrontendMetaDataLink } from "@shared/data/client/links/getFrontendMetaDataLink/getFrontendMetaDataLink";
import { getPersistedQueriesLink } from "@shared/data/client/links/getPersistedQueriesLink/getPersistedQueriesLink";
import { getQueryLoadingLink } from "@shared/data/client/links/getQueryLoadingLink/getQueryLoadingLink";
import { getShouldFrontendRefreshLink } from "@shared/data/client/links/getShouldFrontendRefreshLink/getShouldFrontendRefreshLink";
import { initializeEventIds } from "@shared/helpers/eventIdManager";
import { isProduction, isDevelopment } from "@shared/utils/fullscriptEnv/fullscriptEnv";

import { getCache } from "./cache";

const useClient = (): ApolloClient<NormalizedCacheObject> => {
  const { criticalSnackbar } = useSnackbar();

  const getClient = () => {
    initializeEventIds();

    const getLink = () => {
      const errorLink = getErrorLink({ criticalSnackbar, disableUnauthorizedCheck: true });

      let links = [
        errorLink,
        getFrontendMetaDataLink(),
        getAddUTMLink(),
        getAddEventIdLink(),
        getShouldFrontendRefreshLink(),
        getPersistedQueriesLink(),
      ];

      if (isDevelopment()) {
        const queryLoadingLink = getQueryLoadingLink();
        links = [...links, queryLoadingLink];
      }

      links.push(fingerprintLink);

      return ApolloLink.from([...links, createClientHttpLink()]);
    };

    return new ApolloClient({
      link: getLink(),
      cache: getCache(),
      connectToDevTools: !isProduction(),
    });
  };

  return useMemo(() => getClient(), []);
};

export { useClient };
